import React, { useEffect } from 'react';
import { Award, Users, Target, Heart, CheckCircle2 } from 'lucide-react';
import AnimatedSection from '../components/AnimatedSection';
import { stats } from '../mockData';
import { trackPageView } from '../utils/analytics';

const About = () => {
  useEffect(() => {
    trackPageView('/about', 'About Us');
  }, []);

  const values = [
    {
      icon: Award,
      title: 'Excellence',
      description: 'Committed to delivering the highest quality service and support'
    },
    {
      icon: Users,
      title: 'Customer First',
      description: 'Your success is our success. We prioritize your needs above all'
    },
    {
      icon: Target,
      title: 'Innovation',
      description: 'Continuously evolving to bring you the latest technology solutions'
    },
    {
      icon: Heart,
      title: 'Integrity',
      description: 'Building trust through transparency and honest partnerships'
    }
  ];

  const timeline = [
    { year: '2004', event: 'Started as a Tally Partner' },
    { year: '2017', event: 'Introduced Tally on Cloud Services' },
    { year: '2020', event: 'Launched Cloud Platform for Tally' },
    { year: '2023', event: 'Expanded Partner Network Pan-India' },
    { year: '2025', event: 'Reached 2000+ Active Clients' }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#0A1F44] to-[#0056D2] py-20">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <AnimatedSection animation="fadeInUp">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              About TallyBharat
            </h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto leading-relaxed">
              We are a global cloud services platform provider enabling businesses across all verticals to scale with confidence. With over 21 years of expertise as a Tally Partner, we deliver enterprise-grade cloud infrastructure, IT solutions, and proprietary technologies designed for performance, security, and agility.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-20 bg-white">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection animation="fadeInUp">
            <div className="max-w-4xl mx-auto text-center space-y-6">
              <div className="text-gray-700 leading-relaxed space-y-4 text-lg">
                <p>
                  Our next-generation Tally on Cloud platform transforms how businesses use Tally — delivering unmatched performance, security, and automation, so you can operate efficiently from anywhere.
                </p>
                <p>
                  Beyond Tally, we offer custom ERP software on Cloud, featuring intelligent automation similar to platforms like Marg and Busy, fully tailored to your unique business requirements.
                </p>
                <p>
                  Whether it's Tally customization, Cloud deployment, or business process automation, we empower organizations to simplify operations and achieve measurable productivity gains.
                </p>
                <p className="font-semibold text-[#0056D2]">
                  As a customer-centric organization, we are committed to providing innovative, reliable, and scalable cloud solutions that help businesses accelerate growth and stay ahead in a digital-first world.
                </p>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Infrastructure Section with b2 image */}
      <section className="py-24 bg-gradient-to-b from-[#F5F7FA] to-white">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <AnimatedSection animation="slideInLeft">
              <img
                src="https://tally-bharat.s3.ap-south-1.amazonaws.com/media/tb-media-b2.jpeg"
                alt="Tally Now Runs on Intel® Platinum Servers"
                className="w-full h-auto rounded-3xl shadow-2xl"
                style={{ maxWidth: '1200px', maxHeight: '1200px' }}
              />
            </AnimatedSection>

            <AnimatedSection animation="slideInRight">
              <div className="space-y-6">
                <div className="inline-block bg-[#0056D2]/10 text-[#0056D2] px-4 py-2 rounded-full text-sm font-semibold">
                  Enterprise Infrastructure
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-[#0A1F44]">
                  Tally Now Runs on Intel® Platinum Servers
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Now with enterprise-grade performance & zero downtime. Experience unmatched speed and reliability.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start space-x-3">
                    <CheckCircle2 className="text-[#0056D2] flex-shrink-0 mt-1" size={20} />
                    <span className="text-gray-700">Intel® Xeon® Platinum processors for maximum performance</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle2 className="text-[#0056D2] flex-shrink-0 mt-1" size={20} />
                    <span className="text-gray-700">99.995% uptime with redundant infrastructure</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle2 className="text-[#0056D2] flex-shrink-0 mt-1" size={20} />
                    <span className="text-gray-700">Lightning-fast data processing and retrieval</span>
                  </li>
                </ul>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Mission Section with a2 image - Enhanced */}
      <section className="py-24 bg-gradient-to-b from-[#F5F7FA] via-white to-[#F5F7FA] relative overflow-hidden">
        {/* Decorative Background Elements */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-[#0056D2] opacity-5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#FF7A00] opacity-5 rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />
        
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection animation="slideInLeft">
              <div className="space-y-8">
                <div className="space-y-4">
                  <div className="inline-flex items-center bg-gradient-to-r from-[#FF7A00]/20 to-[#FF7A00]/10 text-[#FF7A00] px-5 py-2.5 rounded-full text-sm font-semibold border border-[#FF7A00]/30 shadow-sm">
                    <span className="w-2 h-2 bg-[#FF7A00] rounded-full mr-2 animate-pulse"></span>
                    The Future of Business Accounting
                  </div>
                  
                  <h2 className="text-4xl md:text-5xl font-bold text-[#0A1F44] leading-tight">
                    Why Cloud Accounting is Essential for Modern Businesses
                  </h2>
                  
                  <p className="text-xl text-gray-600 leading-relaxed font-medium">
                    Transform your financial operations with cloud technology that delivers unmatched flexibility, security, and scalability for businesses of all sizes.
                  </p>
                </div>

                <div className="bg-gradient-to-br from-white to-[#F5F7FA] rounded-2xl p-6 border border-[#0056D2]/10 shadow-lg">
                  <p className="text-lg text-gray-700 leading-relaxed">
                    In today's fast-paced digital landscape, cloud accounting has evolved from a competitive advantage to an absolute necessity. Traditional desktop-based accounting creates barriers that slow down decision-making and limit business agility.
                  </p>
                </div>

                {/* Key Benefits Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    {
                      icon: '🌐',
                      title: 'Global Accessibility',
                      desc: 'Access your financial data from anywhere in the world, on any device, at any time'
                    },
                    {
                      icon: '🔒',
                      title: 'Bank-Grade Security',
                      desc: 'Military-grade encryption and multi-layer security protocols protect your sensitive data'
                    },
                    {
                      icon: '⚡',
                      title: 'Real-Time Collaboration',
                      desc: 'Multiple team members can work simultaneously with instant data synchronization'
                    },
                    {
                      icon: '💰',
                      title: 'Cost Efficiency',
                      desc: 'Eliminate expensive hardware, IT maintenance, and infrastructure costs'
                    }
                  ].map((benefit, index) => (
                    <AnimatedSection key={index} animation="scaleIn" delay={index * 100}>
                      <div className="bg-white rounded-xl p-5 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-gray-100 group">
                        <div className="text-3xl mb-3 transform group-hover:scale-110 transition-transform duration-300">{benefit.icon}</div>
                        <h4 className="font-bold text-[#0A1F44] mb-2 text-base">{benefit.title}</h4>
                        <p className="text-sm text-gray-600 leading-relaxed">{benefit.desc}</p>
                      </div>
                    </AnimatedSection>
                  ))}
                </div>

                {/* Detailed Benefits List */}
                <div className="space-y-4 bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                  <h3 className="text-xl font-bold text-[#0A1F44] mb-4 flex items-center">
                    <span className="w-1 h-6 bg-[#0056D2] rounded-full mr-3"></span>
                    Complete Business Advantages
                  </h3>
                  <ul className="space-y-4">
                    {[
                      {
                        title: 'Anywhere, Anytime Access',
                        desc: 'Work from office, home, or on-the-go with seamless cloud connectivity across all devices'
                      },
                      {
                        title: 'Live Team Collaboration',
                        desc: 'Accountants, auditors, and managers can work together in real-time without version conflicts'
                      },
                      {
                        title: 'Automated Backups & Recovery',
                        desc: 'Never lose data again with continuous automatic backups and point-in-time recovery'
                      },
                      {
                        title: 'Infinite Scalability',
                        desc: 'Grow from 2 users to 200 users without worrying about hardware limitations or upgrades'
                      },
                      {
                        title: 'Zero IT Overhead',
                        desc: 'Eliminate server maintenance, software updates, and technical troubleshooting hassles'
                      },
                      {
                        title: 'Enhanced Compliance',
                        desc: 'Maintain audit trails, access logs, and regulatory compliance with automated reporting'
                      },
                      {
                        title: 'Disaster Recovery',
                        desc: 'Business continuity guaranteed with redundant infrastructure across multiple data centers'
                      },
                      {
                        title: 'Performance & Speed',
                        desc: 'Experience lightning-fast processing with enterprise-grade cloud infrastructure'
                      }
                    ].map((item, index) => (
                      <li key={index} className="flex items-start space-x-4 group">
                        <div className="flex-shrink-0 mt-1">
                          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#0056D2] to-[#0A1F44] flex items-center justify-center shadow-md group-hover:scale-110 transition-transform duration-300">
                            <CheckCircle2 className="text-white" size={18} />
                          </div>
                        </div>
                        <div className="flex-1">
                          <span className="font-semibold text-[#0A1F44] block mb-1">{item.title}</span>
                          <span className="text-gray-600 text-sm leading-relaxed">{item.desc}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Call to Action */}
                <div className="bg-gradient-to-r from-[#0056D2] to-[#0A1F44] rounded-2xl p-6 text-white shadow-xl">
                  <p className="text-lg font-semibold mb-2">Ready to Transform Your Accounting?</p>
                  <p className="text-blue-100 text-sm">Join thousands of businesses that have already made the smart switch to cloud accounting.</p>
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection animation="slideInRight">
              <div className="relative">
                {/* Decorative elements around image */}
                <div className="absolute -top-6 -right-6 w-32 h-32 bg-gradient-to-br from-[#0056D2]/20 to-[#FF7A00]/20 rounded-3xl blur-2xl" />
                <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-gradient-to-br from-[#FF7A00]/20 to-[#0056D2]/20 rounded-3xl blur-2xl" />
                
                <div className="relative rounded-3xl overflow-hidden shadow-2xl border-4 border-white">
                  <img
                    src="https://tally-bharat.s3.ap-south-1.amazonaws.com/media/tb-media-a2.jpeg"
                    alt="Cloud Accounting Excellence"
                    className="w-full h-auto transform hover:scale-105 transition-transform duration-700"
                    style={{ maxWidth: '800px', maxHeight: '1000px' }}
                  />
                  
                  {/* Overlay badge */}
                  <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-md rounded-2xl p-4 shadow-xl border border-gray-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Trusted by</p>
                        <p className="text-2xl font-bold text-[#0056D2]">2000+ Businesses</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-600 mb-1">Uptime</p>
                        <p className="text-2xl font-bold text-[#FF7A00]">99.995%</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <AnimatedSection key={index} animation="scaleIn" delay={index * 100}>
                <div className="text-center bg-gradient-to-br from-[#F5F7FA] to-white rounded-2xl p-8 shadow-lg">
                  <div className="text-4xl md:text-5xl font-bold text-[#0056D2] mb-2">{stat.value}</div>
                  <div className="text-gray-600 font-medium">{stat.label}</div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-24 bg-gradient-to-b from-[#F5F7FA] to-white">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection animation="fadeInUp">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-[#0A1F44] mb-4">
                Our Journey
              </h2>
              <p className="text-xl text-gray-600">
                Two decades of innovation and growth
              </p>
            </div>
          </AnimatedSection>

          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-[#0056D2]/20 hidden lg:block" />

            <div className="space-y-12">
              {timeline.map((item, index) => (
                <AnimatedSection
                  key={index}
                  animation={index % 2 === 0 ? 'slideInLeft' : 'slideInRight'}
                  delay={index * 100}
                >
                  <div
                    className={`flex items-center gap-8 ${
                      index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                    } flex-col`}
                  >
                    <div className={`flex-1 ${index % 2 === 0 ? 'lg:text-right' : 'lg:text-left'} text-center`}>
                      <div className="bg-white rounded-2xl p-6 shadow-lg inline-block">
                        <div className="text-3xl font-bold text-[#0056D2] mb-2">{item.year}</div>
                        <p className="text-gray-700 font-medium">{item.event}</p>
                      </div>
                    </div>
                    <div className="hidden lg:block w-6 h-6 rounded-full bg-[#FF7A00] border-4 border-white shadow-lg relative z-10" />
                    <div className="flex-1" />
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 bg-white">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection animation="fadeInUp">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-[#0A1F44] mb-4">
                Our Core Values
              </h2>
              <p className="text-xl text-gray-600">
                The principles that guide everything we do
              </p>
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <AnimatedSection key={index} animation="scaleIn" delay={index * 100}>
                  <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 text-center group">
                    <div className="bg-gradient-to-br from-[#0056D2] to-[#0A1F44] w-16 h-16 rounded-2xl flex items-center justify-center mb-6 mx-auto group-hover:scale-110 transition-transform duration-300">
                      <Icon size={32} className="text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-[#0A1F44] mb-3">{value.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{value.description}</p>
                  </div>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section - Additional Content */}
      <section className="py-24 bg-gradient-to-b from-[#F5F7FA] to-white">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection animation="fadeInUp">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-[#0A1F44] mb-4">
                What Sets Us Apart
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Experience the TallyBharat difference with our commitment to excellence, innovation, and customer success
              </p>
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Left Column */}
            <AnimatedSection animation="slideInLeft">
              <div className="space-y-8">
                <div className="bg-white rounded-2xl p-8 shadow-lg border-l-4 border-[#0056D2]">
                  <h3 className="text-2xl font-bold text-[#0A1F44] mb-4">🏆 Industry Leadership</h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    With over 21 years as a Tally Partner, we've pioneered cloud hosting solutions for Indian businesses. Our deep understanding of Tally and commitment to innovation keeps us at the forefront of the industry.
                  </p>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-center space-x-2">
                      <CheckCircle2 size={16} className="text-[#0056D2]" />
                      <span>2000+ satisfied customers nationwide</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <CheckCircle2 size={16} className="text-[#0056D2]" />
                      <span>99.5% uptime with enterprise infrastructure</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white rounded-2xl p-8 shadow-lg border-l-4 border-[#FF7A00]">
                  <h3 className="text-2xl font-bold text-[#0A1F44] mb-4">💡 Innovation & Technology</h3>
                  <p className="text-gray-700 leading-relaxed">
                    We continuously invest in cutting-edge technology and infrastructure to provide you with the best cloud hosting experience. From automated backups to advanced security protocols, we ensure your data is always protected and accessible.
                  </p>
                </div>
              </div>
            </AnimatedSection>

            {/* Right Column */}
            <AnimatedSection animation="slideInRight">
              <div className="space-y-8">
                <div className="bg-white rounded-2xl p-8 shadow-lg border-l-4 border-[#0056D2]">
                  <h3 className="text-2xl font-bold text-[#0A1F44] mb-4">🤝 Customer-Centric Approach</h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Your success is our priority. Our dedicated support team is available 24/7 to assist you with any queries or issues. We don't just provide hosting; we become your technology partner.
                  </p>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-center space-x-2">
                      <CheckCircle2 size={16} className="text-[#0056D2]" />
                      <span>Personalized onboarding assistance</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <CheckCircle2 size={16} className="text-[#0056D2]" />
                      <span>Regular training sessions & updates</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white rounded-2xl p-8 shadow-lg border-l-4 border-[#FF7A00]">
                  <h3 className="text-2xl font-bold text-[#0A1F44] mb-4">🔒 Security & Compliance</h3>
                  <p className="text-gray-700 leading-relaxed">
                    We understand the critical nature of your financial data. Our ISO-certified infrastructure, multi-layer security protocols, and compliance with industry standards ensure your data remains secure and confidential at all times.
                  </p>
                </div>
              </div>
            </AnimatedSection>
          </div>

          {/* Stats Row */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { number: '2000+', label: 'Happy Customers' },
              { number: '99.5%', label: 'Uptime SLA' },
              { number: '24/7', label: 'Support Available' },
              { number: '21+', label: 'Years Experience' }
            ].map((stat, index) => (
              <AnimatedSection key={index} animation="fadeInUp" delay={index * 100}>
                <div className="text-center p-6 bg-white rounded-xl shadow-md">
                  <div className="text-4xl font-bold text-[#0056D2] mb-2">{stat.number}</div>
                  <div className="text-gray-600 font-medium">{stat.label}</div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications Section */}
      <section className="py-24 bg-gradient-to-b from-[#F5F7FA] to-white">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection animation="fadeInUp">
            <div className="bg-gradient-to-r from-[#0056D2] to-[#0A1F44] rounded-3xl p-12 text-center text-white">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Certified & Trusted
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
                {[
                  { title: 'Tally Partner', subtitle: 'Since 2004' },
                  { title: 'ISO Certified', subtitle: 'Infrastructure' },
                  { title: 'Industry Recognized', subtitle: 'Excellence Awards' }
                ].map((cert, index) => (
                  <div key={index} className="flex items-center justify-center space-x-3">
                    <CheckCircle2 size={32} className="text-[#FF7A00] flex-shrink-0" />
                    <div className="text-left">
                      <div className="font-semibold text-lg">{cert.title}</div>
                      <div className="text-blue-100 text-sm">{cert.subtitle}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default About;